import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PlusIcon,
  TrashIcon,
  PencilSimpleIcon,
  CheckIcon,
  XIcon,
  MagnifyingGlassIcon,
} from "@phosphor-icons/react";

export default function BrandsManager() {
  const [brands, setBrands] = useState([]);
  const [selected, setSelected] = useState(null); // selected brand object
  const [search, setSearch] = useState("");
  const [newBrand, setNewBrand] = useState("");
  const [newModel, setNewModel] = useState("");
  const [editingModel, setEditingModel] = useState(null); // model name being renamed
  const [editingModelValue, setEditingModelValue] = useState("");
  const [editingBrandName, setEditingBrandName] = useState(false);
  const [brandRename, setBrandRename] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/catalog/brands");
    const items = r.data.items || [];
    setBrands(items);
    setSelected((sel) => {
      if (sel) {
        const upd = items.find((b) => b.id === sel.id);
        return upd || sel;
      }
      return items.length > 0 ? items[0] : null;
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const sortedSelectedModels = useMemo(
    () => (selected?.models ? [...selected.models].sort() : []),
    [selected]
  );

  const addBrand = async () => {
    const n = newBrand.trim();
    if (!n) return;
    try {
      await api.post("/catalog/brands", { name: n, models: [] });
      toast.success(`Προστέθηκε «${n}»`);
      setNewBrand("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
    }
  };

  const deleteBrand = async (b) => {
    if (!window.confirm(`Διαγραφή «${b.name}» και όλων των μοντέλων;`)) return;
    try {
      await api.delete(`/catalog/brands/${b.id}`);
      toast.success("Διαγράφηκε");
      setSelected(null);
      load();
    } catch (e) {
      toast.error("Αποτυχία");
    }
  };

  const renameBrand = async () => {
    const n = brandRename.trim();
    if (!n || !selected) return;
    try {
      await api.put(`/catalog/brands/${selected.id}`, { name: n });
      toast.success("Ενημερώθηκε");
      setEditingBrandName(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
    }
  };

  const addModel = async () => {
    const m = newModel.trim();
    if (!m || !selected) return;
    try {
      await api.post(`/catalog/brands/${selected.id}/models`, { model_name: m });
      toast.success(`Προστέθηκε «${m}»`);
      setNewModel("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
    }
  };

  const deleteModel = async (m) => {
    if (!selected) return;
    if (!window.confirm(`Διαγραφή μοντέλου «${m}»;`)) return;
    try {
      await api.delete(`/catalog/brands/${selected.id}/models/${encodeURIComponent(m)}`);
      toast.success("Διαγράφηκε");
      load();
    } catch (e) {
      toast.error("Αποτυχία");
    }
  };

  const saveRenameModel = async () => {
    if (!selected || !editingModel) return;
    const nv = editingModelValue.trim();
    if (!nv || nv === editingModel) {
      setEditingModel(null);
      return;
    }
    try {
      await api.put(`/catalog/brands/${selected.id}/models`, {
        model_name: editingModel,
        new_name: nv,
      });
      toast.success("Ενημερώθηκε");
      setEditingModel(null);
      load();
    } catch (e) {
      toast.error("Αποτυχία");
    }
  };

  const filteredBrands = brands.filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="brands-page">
      <div className="text-sm text-[#4B5563] max-w-2xl">
        Διαχειριστείτε τις μάρκες και τα μοντέλα που εμφανίζονται στη φόρμα προσθήκης αυτοκινήτου. Οι αλλαγές αποθηκεύονται και χρησιμοποιούνται από όλο το σύστημα.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-0 border border-black/10 min-h-[600px]">
        {/* Brands list */}
        <div className="md:col-span-4 border-r border-black/10 flex flex-col">
          <div className="p-3 border-b border-black/10 bg-[#F9FAFB]">
            <div className="flex items-center gap-2 mb-2">
              <MagnifyingGlassIcon size={16} color="#4B5563" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Αναζήτηση μάρκας..."
                data-testid="brand-search"
                className="flex-1 bg-transparent outline-none text-sm"
              />
            </div>
            <div className="flex gap-2">
              <input
                value={newBrand}
                onChange={(e) => setNewBrand(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addBrand()}
                placeholder="Νέα μάρκα"
                data-testid="new-brand-input"
                className="flex-1 px-2 py-1.5 border border-black/20 bg-white text-sm outline-none focus:border-[#0055FF]"
              />
              <button onClick={addBrand} className="btn-primary text-xs px-3 py-1.5" data-testid="add-brand-btn">
                <PlusIcon size={14} />
              </button>
            </div>
          </div>
          <div className="overflow-y-auto flex-1 max-h-[600px]">
            {filteredBrands.map((b) => (
              <button
                key={b.id}
                onClick={() => {
                  setSelected(b);
                  setEditingBrandName(false);
                  setEditingModel(null);
                }}
                data-testid={`brand-item-${b.name}`}
                className={`w-full text-left px-4 py-2.5 border-b border-black/5 flex items-center justify-between transition-colors ${
                  selected?.id === b.id ? "bg-[#0A0A0A] text-white" : "hover:bg-[#F9FAFB]"
                }`}
              >
                <div>
                  <div className="text-sm font-medium">{b.name}</div>
                  <div className={`text-[10px] font-mono ${selected?.id === b.id ? "text-white/60" : "text-[#4B5563]"}`}>
                    {b.models.length} μοντέλα {b.is_default ? "· default" : "· custom"}
                  </div>
                </div>
              </button>
            ))}
            {filteredBrands.length === 0 && (
              <div className="p-6 text-center text-sm text-[#4B5563]">Καμία μάρκα</div>
            )}
          </div>
        </div>

        {/* Brand detail */}
        <div className="md:col-span-8 p-6">
          {!selected ? (
            <div className="text-center py-16 text-[#4B5563]">Επιλέξτε μάρκα από αριστερά</div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                {editingBrandName ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      value={brandRename}
                      onChange={(e) => setBrandRename(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && renameBrand()}
                      data-testid="brand-rename-input"
                      className="text-2xl heading font-extrabold px-2 py-1 border border-[#0055FF] outline-none"
                      autoFocus
                    />
                    <button onClick={renameBrand} className="p-2 bg-[#16A34A] text-white" data-testid="brand-rename-save">
                      <CheckIcon size={16} />
                    </button>
                    <button onClick={() => setEditingBrandName(false)} className="p-2 border border-black/20">
                      <XIcon size={16} />
                    </button>
                  </div>
                ) : (
                  <h2 className="heading text-2xl font-extrabold tracking-tight">{selected.name}</h2>
                )}
                <div className="flex gap-1">
                  {!editingBrandName && (
                    <button
                      onClick={() => {
                        setEditingBrandName(true);
                        setBrandRename(selected.name);
                      }}
                      className="p-2 hover:bg-black/5"
                      title="Μετονομασία"
                      data-testid="rename-brand-btn"
                    >
                      <PencilSimpleIcon size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => deleteBrand(selected)}
                    className="p-2 hover:bg-[#E60000] hover:text-white"
                    title="Διαγραφή μάρκας"
                    data-testid="delete-brand-btn"
                  >
                    <TrashIcon size={16} />
                  </button>
                </div>
              </div>

              <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563] mb-3">
                Μοντέλα ({selected.models.length})
              </div>

              <div className="flex gap-2 mb-4 pb-4 border-b border-black/10">
                <input
                  value={newModel}
                  onChange={(e) => setNewModel(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addModel()}
                  placeholder="Νέο μοντέλο"
                  data-testid="new-model-input"
                  className="flex-1 px-3 py-2 border border-black/20 bg-white text-sm outline-none focus:border-[#0055FF]"
                />
                <button onClick={addModel} className="btn-primary text-sm flex items-center gap-1.5" data-testid="add-model-btn">
                  <PlusIcon size={14} /> Προσθήκη
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {sortedSelectedModels.map((m) => (
                  <div
                    key={m}
                    className="flex items-center justify-between px-3 py-2 border border-black/10 hover:bg-[#F9FAFB] group"
                    data-testid={`model-row-${m}`}
                  >
                    {editingModel === m ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          value={editingModelValue}
                          onChange={(e) => setEditingModelValue(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && saveRenameModel()}
                          className="flex-1 px-2 py-1 border border-[#0055FF] outline-none text-sm"
                          autoFocus
                        />
                        <button onClick={saveRenameModel} className="p-1 bg-[#16A34A] text-white">
                          <CheckIcon size={12} />
                        </button>
                        <button onClick={() => setEditingModel(null)} className="p-1 border border-black/20">
                          <XIcon size={12} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="text-sm">{m}</span>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <button
                            onClick={() => {
                              setEditingModel(m);
                              setEditingModelValue(m);
                            }}
                            className="p-1 hover:bg-black/5"
                            data-testid={`rename-model-${m}`}
                          >
                            <PencilSimpleIcon size={12} />
                          </button>
                          <button
                            onClick={() => deleteModel(m)}
                            className="p-1 hover:bg-[#E60000] hover:text-white"
                            data-testid={`delete-model-${m}`}
                          >
                            <TrashIcon size={12} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
                {selected.models.length === 0 && (
                  <div className="col-span-full text-sm text-[#4B5563] py-6 text-center">
                    Κανένα μοντέλο. Προσθέστε ένα παραπάνω.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
